import React, { useEffect, useRef, useState } from 'react';
import { ChatMessageProps } from '@/types/chat';
import { CurveChart } from '@/components/chat/';
import { DrainDetection } from '@/components/chat/';
import { ReasoningSteps } from '@/components/chat/';
import { CodeBlock } from '@/components/chat/CodeBlock';
import { UIResourceBlock } from '@/components/chat/UIResourceBlock';
import { cn } from '@/lib/utils';
import { ArrowRight, Check, Copy, Pencil } from 'lucide-react';
import { formatMessageTimestamp } from '@/utils/formatTimestamp';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import AssistantIcon from '@/components/icons/AssistantIcon';
import { MessageText } from '@/components/chat/';
import { Feedback } from '@/components/chat/';
import { CSVPreview } from '@/components/chat/';
import { formatFileSize, getFileIcon } from '@/services/attachmentsApi';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';
import { ContextNotePill } from '@/components/knowledge/ContextNotePill';

export const ChatMessage = ({
  message,
  onComponentAction,
  onSelectNextPrompt,
  onEditPrompt,
  conversation_id,
  streamingStatus,
}: ChatMessageProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const { copied, copy } = useCopyToClipboard();
  const editRef = useRef<HTMLTextAreaElement>(null);
  const isUser = message.role === 'user';
  const blocks = message.blocks;
  const useBlocks = blocks && blocks.length > 0;

  const hasReasoningSteps =
    message.reasoningSteps && message.reasoningSteps.length > 0;

  // `autoFocus` is disallowed by the a11y lint rules.
  useEffect(() => {
    if (isEditing) editRef.current?.focus();
  }, [isEditing]);

  if (
    blocks !== undefined &&
    blocks.length === 0 &&
    (!message.content ||
      (typeof message.content === 'string' && !message.content.trim())) &&
    (!message.uploadedFiles || message.uploadedFiles.length === 0) &&
    !hasReasoningSteps &&
    !streamingStatus
  ) {
    return null;
  }

  const messageTextContent = useBlocks
    ? blocks
        ?.filter(b => b.type === 'text')
        .map(b => b.data)
        .join('\n') ?? ''
    : typeof message.content === 'string'
    ? message.content
    : '';
  const timestampLabel = formatMessageTimestamp(message.timestamp);

  // Lengths only — prompt text is user content and doesn't belong in telemetry.
  const track = (event: string, attributes: Record<string, string> = {}) =>
    trackAdpWaypoint(getBernoulliAdp(), event, {
      conversation_id: conversation_id || '',
      request_id: message.request_id || '',
      ...attributes,
    });

  const handleCopyPrompt = async () => {
    if (await copy(messageTextContent)) {
      track(ADP_EVENTS.CHAT_COPY_PROMPT, {
        prompt_length: String(messageTextContent.length),
      });
    }
  };

  const handleEditOpen = () => {
    setDraft(messageTextContent);
    setIsEditing(true);
    track(ADP_EVENTS.CHAT_EDIT_PROMPT_OPEN);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setDraft(messageTextContent);
  };

  const handleEditSubmit = () => {
    const edited = draft.trim();
    if (!edited || !onEditPrompt) return;
    setIsEditing(false);
    track(ADP_EVENTS.CHAT_EDIT_PROMPT_SEND, {
      prompt_length: String(edited.length),
      original_length: String(messageTextContent.length),
    });
    onEditPrompt(edited);
  };

  const nextPrompts =
    message.transparencySummary?.nextPrompts?.filter(
      prompt =>
        (typeof prompt === 'string' && prompt.trim().length > 0) ||
        (typeof prompt === 'object' &&
          prompt !== null &&
          'label' in prompt &&
          'prompt' in prompt),
    ) ?? [];

  const renderTextBlock = (text: string, index: number) => {
    const isStreamingMessage = !message.messageCompleted && !!streamingStatus;
    return (
      <div
        key={`text-${index}`}
        className={cn(
          isUser
            ? 'rounded-2xl px-4 py-3 bg-[#E2E7EB] text-[#252F33] shadow-sm'
            : 'text-[#252F33]',
        )}>
        <div className="break-words text-sm sm:text-base leading-relaxed">
          <MessageText
            text={text}
            isUser={isUser}
            isStreaming={isStreamingMessage}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col',
        isUser ? 'items-end group' : 'items-start',
      )}>
      {/* Assistant label */}
      {!isUser && (
        <div className="flex items-center gap-2 mb-2">
          <AssistantIcon size={20} className="text-[#3C3C3C]" />
          <span className="text-[15px] font-semibold text-[#161618]">
            Autodesk Assistant
          </span>
        </div>
      )}

      {/* Reasoning steps - shown before response content */}
      {!isUser && (hasReasoningSteps || streamingStatus) && (
        <ReasoningSteps
          steps={message.reasoningSteps || []}
          isComplete={message.messageCompleted}
          streamingStatus={streamingStatus}
          isRestored={message.isRestored}
          restoredDurationSec={message.restoredDurationSec}
          transparencySummary={message.transparencySummary}
        />
      )}

      {isUser && isEditing && (
        <div className="flex w-full max-w-[85%] flex-col gap-2">
          <textarea
            ref={editRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleEditSubmit();
              }
              if (e.key === 'Escape') {
                handleEditCancel();
              }
            }}
            rows={3}
            aria-label="Edit your message"
            className="w-full resize-y rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm leading-relaxed text-[#252F33] shadow-sm focus:border-[#006EAF] focus:outline-none sm:text-base"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleEditCancel}
              className="rounded-lg px-3 py-1.5 text-sm text-[#252F33] transition-colors hover:bg-[#E8EAEF]">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEditSubmit}
              disabled={!draft.trim() || !onEditPrompt}
              className="rounded-lg bg-[#7A8C94] px-3 py-1.5 text-sm text-white transition-colors hover:bg-[#6A7C84] disabled:cursor-not-allowed disabled:opacity-40">
              Send
            </button>
          </div>
        </div>
      )}

      {/* Message content */}
      {!(isUser && isEditing) && (
        <div
          className={cn(
            'flex flex-col gap-1.5',
            isUser ? 'max-w-[85%] items-end' : 'w-full',
          )}>
          {useBlocks ? (
            blocks?.map((block, index) => {
              if (block.type === 'text') {
                return renderTextBlock(block.data, index);
              }

              if (block.type === 'charts') {
                const charts = Array.isArray(block.data)
                  ? block.data
                  : [block.data];
                return (
                  <div
                    key={`charts-${index}`}
                    className={cn(
                      'w-full gap-4',
                      index > 0 && 'mt-2',
                      charts.length === 1
                        ? 'flex flex-col'
                        : 'grid grid-cols-1 md:grid-cols-2',
                    )}>
                    {charts.map((chart: any, idx: number) => (
                      <CurveChart
                        key={idx}
                        config={chart.config}
                        onAction={onComponentAction}
                      />
                    ))}
                  </div>
                );
              }

              if (block.type === 'code') {
                return (
                  <div key={`code-${index}`} className="w-full">
                    <CodeBlock
                      code={block.data?.script || ''}
                      language={block.data?.language || 'plaintext'}
                      description={block.data?.description}
                    />
                  </div>
                );
              }

              if (block.type === 'files') {
                const csvFiles = block.data?.filter(
                  (file: any) =>
                    file.filename?.toLowerCase().endsWith('.csv') &&
                    file.headers,
                );

                if (csvFiles && csvFiles.length > 0) {
                  return (
                    <div key={`files-${index}`} className="w-full mt-2">
                      {csvFiles.map((file: any, idx: number) => (
                        <CSVPreview
                          key={idx}
                          files={[]}
                          csvMetadata={{
                            headers: file.headers,
                            sample_rows: file.sample_rows,
                            row_count: file.row_count,
                            filename: file.filename,
                          }}
                        />
                      ))}
                    </div>
                  );
                }

                return (
                  <div key={`files-${index}`} className="w-full mt-2 space-y-2">
                    {block.data?.map((file: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100">
                          <span className="text-xl">📎</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#252F33] truncate">
                            {file.filename}
                          </div>
                          <div className="text-xs text-[#768D95]">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }

              if (block.type === 'ui') {
                return (
                  <div key={`ui-${index}`} className="w-full mt-2">
                    <UIResourceBlock
                      resources={block.data || []}
                      onAction={onComponentAction}
                    />
                  </div>
                );
              }

              if (block.type === 'drain_detection') {
                return (
                  <div key={`drain_detection-${index}`} className="w-full mt-2">
                    <DrainDetection data={block.data} />
                  </div>
                );
              }

              return null;
            })
          ) : (
            <>
              {isUser &&
                message.extras?.context &&
                message.extras.context.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                    <span className="text-[11px] text-[#768D95]">
                      {message.extras.context.length === 1
                        ? 'Context:'
                        : `Context (${message.extras.context.length}):`}
                    </span>
                    {message.extras.context.map(c => (
                      <ContextNotePill
                        key={c.context_id}
                        name={c.name}
                        scope={c.scope}
                      />
                    ))}
                  </div>
                )}

              {typeof message.content === 'string' &&
                message.content.trim() && (
                  <div
                    className={cn(
                      isUser
                        ? 'rounded-2xl px-4 py-3 bg-[#E2E7EB] text-[#252F33] shadow-sm'
                        : 'text-[#252F33]',
                    )}>
                    <div className="break-words text-sm sm:text-base leading-relaxed">
                      <MessageText
                        text={message.content}
                        isUser={isUser}
                        isStreaming={
                          !message.messageCompleted && !!streamingStatus
                        }
                      />
                    </div>
                  </div>
                )}

              {isUser &&
                message.extras?.files &&
                message.extras.files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {message.extras.files.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#DDE3E6] text-[#252F33] text-sm">
                        <span
                          className="text-base"
                          role="img"
                          aria-label="file">
                          {getFileIcon(file.filename)}
                        </span>
                        <span className="max-w-[150px] truncate">
                          {file.filename}
                        </span>
                        <span className="text-[#768D95] text-xs">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

              {message.uploadedFiles && message.uploadedFiles.length > 0 && (
                <CSVPreview
                  files={message.uploadedFiles}
                  csvMetadata={
                    message.extras?.files?.[0]
                      ? {
                          headers: message.extras.files[0].headers,
                          sample_rows: message.extras.files[0].sample_rows,
                          row_count: message.extras.files[0].row_count,
                          filename: message.extras.files[0].filename,
                        }
                      : undefined
                  }
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Revealed on hover, always shown where hover doesn't exist. */}
      {isUser && !isEditing && messageTextContent && (
        <div className="mt-1 flex items-center gap-1">
          {timestampLabel && (
            <span className="text-[11px] text-[#A0AEB4]">{timestampLabel}</span>
          )}
          <div className="flex items-center gap-1 opacity-0 pointer-events-none transition-opacity focus-within:opacity-100 focus-within:pointer-events-auto group-hover:opacity-100 group-hover:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto">
            <button
              type="button"
              onClick={handleCopyPrompt}
              className="rounded p-1.5 text-[#A0AEB4] transition-colors hover:text-[#252F33]"
              aria-label="Copy prompt">
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
            {onEditPrompt && (
              <button
                type="button"
                onClick={handleEditOpen}
                className="rounded p-1.5 text-[#A0AEB4] transition-colors hover:text-[#252F33]"
                aria-label="Edit prompt">
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {!isUser &&
        message.messageCompleted &&
        message.skillsApplied &&
        message.skillsApplied.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[11px] text-[#768D95]">Skills Used:</span>
            {message.skillsApplied.map(s => (
              <span key={s.skill_id} className="inline-flex items-center gap-1">
                <span className="text-[11px] text-[#252F33]">{s.name}</span>
                {s.scope === 'tenant' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    tenant
                  </span>
                )}
                {s.auto_triggered ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                    auto
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                    manual
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

      {!isUser &&
        message.messageCompleted &&
        message.contextApplied &&
        message.contextApplied.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-[#768D95]">
              {message.contextApplied.some(c => c.item_type === 'document')
                ? message.contextApplied.length === 1
                  ? 'Sources cited:'
                  : `Sources cited (${message.contextApplied.length}):`
                : message.contextApplied.length === 1
                ? 'Context used:'
                : `Context used (${message.contextApplied.length}):`}
            </span>
            {message.contextApplied.map(c => (
              <ContextNotePill
                key={`${c.context_id}-${c.passage_index ?? 'all'}`}
                name={
                  c.passage_index ?? c.block_index
                    ? `${c.name} · block ${c.passage_index ?? c.block_index}`
                    : c.name
                }
                scope={c.scope}
                tags={
                  <>
                    {c.item_type === 'document' && (
                      <span className="text-[10px] uppercase tracking-wide px-1 rounded bg-sky-100/80 text-sky-800">
                        Source
                      </span>
                    )}
                    {c.apply_mode === 'always' && (
                      <span className="text-[10px] uppercase tracking-wide px-1 rounded bg-emerald-100/80 text-emerald-800">
                        Always
                      </span>
                    )}
                    {c.manually_applied && c.apply_mode !== 'always' && (
                      <span className="text-[10px] uppercase tracking-wide px-1 rounded bg-white/60 text-[#768D95]">
                        Applied
                      </span>
                    )}
                  </>
                }
              />
            ))}
          </div>
        )}

      {!isUser &&
        message.messageCompleted &&
        message.contextApplied?.some(c => c.excerpt) && (
          <div className="mt-1 space-y-1.5">
            {message.contextApplied
              .filter(c => c.excerpt)
              .map(c => (
                <div
                  key={`excerpt-${c.context_id}-${c.passage_index ?? 0}`}
                  className="rounded-md border border-slate-200 bg-[#FAFBFC] px-3 py-2">
                  <p className="text-[11px] font-medium text-[#252F33]">
                    {c.name}
                    {c.passage_index ?? c.block_index
                      ? ` · block ${c.passage_index ?? c.block_index}`
                      : ''}
                  </p>
                  <p className="text-[11px] text-[#768D95] mt-1 leading-relaxed">
                    {c.excerpt}
                  </p>
                </div>
              ))}
          </div>
        )}

      {!isUser &&
        message.messageCompleted &&
        (timestampLabel || message.request_id) && (
          <div className="mt-1.5 mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {timestampLabel && (
              <span className="text-[11px] text-[#A0AEB4]">
                {timestampLabel}
              </span>
            )}
            {message.request_id && (
              <span className="text-[11px] text-[#A0AEB4] font-mono">
                Request ID: {message.request_id}
              </span>
            )}
          </div>
        )}

      {/* Feedback actions (copy, thumbs up, thumbs down) */}
      {!isUser && message.messageCompleted && (
        <Feedback
          conversation_id={conversation_id}
          request_id={message.request_id}
          existingFeedback={message.existingFeedback}
          messageContent={messageTextContent}
        />
      )}

      {!isUser && message.messageCompleted && nextPrompts.length > 0 && (
        <div className="w-full mt-4 pt-4 border-t border-slate-200">
          <p className="text-sm font-medium text-[#768D95]">
            Suggested follow-ups
          </p>
          <div className="mt-2">
            {nextPrompts.map((item, index) => {
              const label = typeof item === 'string' ? item : item.label;
              const prompt = typeof item === 'string' ? item : item.prompt;
              const extras = typeof item === 'object' ? item.extras : undefined;
              return (
                <button
                  key={`${label}-${index}`}
                  type="button"
                  onClick={() => {
                    trackAdpWaypoint(
                      getBernoulliAdp(),
                      ADP_EVENTS.CHAT_SUGGESTED_FOLLOWUP,
                      { prompt_text: label },
                    );
                    onSelectNextPrompt?.(prompt, extras);
                  }}
                  className="w-full flex items-center justify-between gap-2 py-2 text-left text-sm text-[#252F33] hover:text-[#006EAF] transition-colors">
                  <span className="flex-1">{label}</span>
                  <ArrowRight className="h-4 w-4 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
